import React, { ReactElement, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  TextField,
} from '@mui/material';
import { ContentCopy as CopyIcon, Close as CloseIcon } from '@mui/icons-material';
import { gray40, primaryButtonBackgroundColor } from '../../../constants/colors';
import { dialogTitle as dialogTitleStyle } from '../../../constants/location';
import { createReferralInvitation } from '../../../api/firebase/function/referralFunction';
import { referralMessages } from '../../../constants/messages';
import CustomSnackbar from '../form/CustomSnackbar';

interface ReferralInviteDialogProps {
  open: boolean;
  onClose: () => void;
}

/**
 * リファラル招待ダイアログ
 * 招待リンクの生成・表示・コピーを行う共通ダイアログ
 */
const ReferralInviteDialog = ({ open, onClose }: ReferralInviteDialogProps): ReactElement => {
  const [referralUrl, setReferralUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarErrorText, setSnackbarErrorText] = useState('');
  const [snackbarInfoText, setSnackbarInfoText] = useState('');

  // ダイアログが開いたときにリンクを生成
  React.useEffect(() => {
    if (open && !referralUrl) {
      setLoading(true);
      createReferralInvitation()
        .then(response => {
          if (response.success) {
            setReferralUrl(response.data.referral_url);
          }
        })
        .catch((error: unknown) => {
          const message =
            error && typeof error === 'object' && 'message' in error
              ? (error as { message: string }).message
              : referralMessages.createFailed;
          setSnackbarErrorText(message);
          setSnackbarInfoText('');
          setSnackbarOpen(true);
          onClose();
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [open, referralUrl, onClose]);

  const handleCopyLink = async (): Promise<void> => {
    if (!referralUrl) return;
    try {
      await navigator.clipboard.writeText(referralUrl);
      setSnackbarInfoText(referralMessages.linkCopied);
      setSnackbarErrorText('');
      setSnackbarOpen(true);
    } catch {
      setSnackbarErrorText(referralMessages.copyFailed);
      setSnackbarInfoText('');
      setSnackbarOpen(true);
    }
  };

  const handleSnackbarClose = (): void => {
    setSnackbarOpen(false);
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="xs"
        fullWidth
        sx={{
          '& .MuiPaper-root': {
            border: `1px solid ${gray40}`,
            boxSizing: 'border-box',
            margin: '16px',
            padding: '40px',
            '@media screen and (max-width: 960px)': {
              padding: '32px 16px',
            },
          },
        }}
      >
        <DialogTitle
          sx={{
            ...dialogTitleStyle,
            padding: 0,
            marginBottom: '24px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          {referralMessages.dialogTitle}
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ padding: 0, overflow: 'visible' }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', padding: '24px' }}>
              <CircularProgress size={32} />
            </Box>
          ) : (
            <TextField
              label={referralMessages.inviteLinkLabel}
              value={referralUrl}
              fullWidth
              InputProps={{
                readOnly: true,
                sx: {
                  '& input': {
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  },
                },
              }}
            />
          )}
        </DialogContent>
        <DialogActions sx={{ padding: 0, marginTop: '24px' }}>
          <Button
            onClick={handleCopyLink}
            disabled={!referralUrl || loading}
            startIcon={<CopyIcon />}
            sx={{
              ...primaryButtonBackgroundColor,
              height: '44px',
              width: '100%',
              fontSize: '14px',
              fontWeight: 700,
            }}
            variant="contained"
          >
            {referralMessages.createAndCopyButton}
          </Button>
        </DialogActions>
      </Dialog>
      <CustomSnackbar
        open={snackbarOpen}
        onClose={handleSnackbarClose}
        text={snackbarErrorText}
        infoText={snackbarInfoText}
      />
    </>
  );
};

export default ReferralInviteDialog;
